// Global
import { ImageField, LinkField, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import React, { useContext } from 'react';
import { useI18n } from 'next-localization';

// Lib
import { ComponentProps } from 'lib/component-props';
import { useTheme } from 'lib/context/ThemeContext';
import { CardComponents, ProductAndArticleComponent } from 'lib/templates/Feature.Dart.model';
import { Common } from 'lib/templates/Foundation.Dart.model';
import { ItemEx } from 'lib/templates/_.Sitecore.Override';
import { getStaticAssetUrl } from 'lib/utils/public-url-utils';

// Local
import { ProductCardContext } from 'components/authorable/ProductCard/ProductCard';
import Button from 'helpers/Button/Button';
import ImageWrapper from 'helpers/ImageWrapper/ImageWrapper';
import Label from 'helpers/Label/Label';
import LinkWrapper from 'helpers/LinkWrapper/LinkWrapper';
import RichTextA11yWrapper from 'helpers/RichTextA11yWrapper/RichTextA11yWrapper';
import { stripHtml } from 'lib/utils/regex';
import { tailwindVariants } from './ProductSingleCardTailwind';
import { productPattern } from 'lib/fallback/fallback';

export type Badge = CardComponents.ProductCard.Badge;

export type Label = Common.Label.Label;

export type ProductInfo = ProductAndArticleComponent.ProductInformation.ProductInformation;

export type ProductCard = ItemEx &
  CardComponents.ProductCard.ProductCardItem &
  Badge & { indexOfItem: number; isBadgeFullWidth: string | undefined };

export type ProductCardProps = ComponentProps & CardComponents.ProductCard.ProductCardsList;

const ProductSingleCard = ({ indexOfItem, ...props }: ProductCard): JSX.Element => {
  const { ctaLink } = props?.fields || {};

  const productCardUid = useContext(ProductCardContext);
  const { sitecoreContext } = useSitecoreContext();
  const enableBazaarSetting = sitecoreContext?.bazaarVoice?.enableBazaarVoice as
    | boolean
    | undefined;

  const enablePowerReviewSetting = sitecoreContext?.powerReviews?.enablePowerReviews as
    | boolean
    | undefined;

  const { themeName } = useTheme();

  const {
    badgeBG,
    badgeContainer,
    cardContainer,
    cardContentWrapper,
    cardImgWrapper,
    cardLinkWrapper,
    cardMedia,
    cardWrapper,
    ctaWrapper,
    labelBg,
    productDescription,
    productInfoContainer,
    productInfoTitleRatingsContainer,
    bazaarVoiceRatingWrapper,
    ratingWrapper,
    productTitle,
  } = tailwindVariants({
    /* eslint-disable  @typescript-eslint/ban-ts-comment */
    // @ts-ignore
    brand: themeName as string,
    isBadgeFullWidth: props?.isBadgeFullWidth as '1' | undefined,
  });

  const badge = props?.fields?.badge as Badge;
  const badgeSVG = `ProductCard/Breakpoint=Medium,Brand=${themeName}`;

  const productInfo = props?.fields?.productInformation as ProductInfo;
  const pageID = productInfo?.fields?.oldPageId?.value || props?.fields?.productInformation?.id;
  const label = productInfo?.fields?.label as unknown as Label[];
  const svgBG = `${getStaticAssetUrl()}/assets/${badgeSVG}.svg`;
  const ariaLabel = stripHtml(productInfo?.fields?.headline?.value as string);
  const i18n = useI18n();
  const About = i18n.t('AccessibilityAbout') ? i18n.t('AccessibilityAbout') : 'about';

  const commonPattern = productPattern?.commonPattern;

  return (
    <div className={cardWrapper()}>
      {badge?.fields?.badgeTitle?.value && (
        <div className={badgeContainer()} aria-hidden="true">
          <span
            className={badgeBG()}
            style={{
              ...(!commonPattern.includes(themeName) && { backgroundImage: `url(${svgBG})` }),
              backgroundSize: 'cover',
              backgroundRepeat: props?.isBadgeFullWidth ? 'no-repeat' : 'repeat',
            }}
          >
            {badge?.fields?.badgeTitle?.value}
          </span>
        </div>
      )}
      {ctaLink?.value?.href && (
        <LinkWrapper
          className={cardImgWrapper()}
          showLinkTextWithChildrenPresent={false}
          field={ctaLink}
          aria-label={ariaLabel}
        >
          <ImageWrapper
            field={productInfo?.fields?.primaryImage as ImageField}
            layout="responsive"
            className={cardMedia()}
          />
        </LinkWrapper>
      )}
      <div className={cardContentWrapper()}>
        <div className={cardContainer()}>
          {label?.length > 0 && label?.[0]?.fields?.link?.value?.href && (
            <Label
              color={'white'}
              link={label?.[0]?.fields?.link as LinkField}
              backgroundColor={labelBg()}
            />
          )}

          <div className={productInfoContainer()}>
            <div className={productInfoTitleRatingsContainer()}>
              {productInfo?.fields?.headline && (
                <div className={productTitle()}>
                  {props?.fields?.ctaLink?.value?.href && (
                    <LinkWrapper
                      className={cardLinkWrapper()}
                      showLinkTextWithChildrenPresent={false}
                      field={props?.fields?.ctaLink}
                      aria-label={ariaLabel}
                    >
                      <RichTextA11yWrapper field={productInfo?.fields?.headline} />
                    </LinkWrapper>
                  )}
                </div>
              )}
              {productInfo?.fields?.ratingAndReview &&
                (pageID && enableBazaarSetting ? (
                  <div
                    data-bv-show="inline_rating"
                    data-bv-product-id={pageID.replace(/-/g, '').toUpperCase()}
                    className={bazaarVoiceRatingWrapper()}
                  ></div>
                ) : (
                  enablePowerReviewSetting && (
                    <div
                      id={`category-snippet-${productCardUid}-${pageID}-${indexOfItem}`}
                      className={ratingWrapper()}
                    ></div>
                  )
                ))}
            </div>
            {productInfo?.fields?.description && (
              <div className={productDescription()}>
                <RichTextA11yWrapper field={productInfo?.fields?.description} />
              </div>
            )}
          </div>
        </div>
        {ctaLink?.value?.href && (
          <div className={ctaWrapper()}>
            <Button
              srOnlyText={ariaLabel}
              label={ctaLink?.value?.text}
              type="fill"
              title={`${ctaLink?.value?.title}` || `${ctaLink?.value?.text} ${About} ${ariaLabel}`}
              tag="a"
              target={ctaLink?.value?.target}
              href={props?.fields?.ctaLink?.value?.href}
              gtmEvent={{
                event: 'cta_click',
                type: 'primary',
                'gtm.element.dataset.gtmLinkUrl': props?.fields?.ctaLink?.value?.href,
                'gtm.element.dataset.gtmLinkName': props?.fields?.ctaLink?.value?.text,
                'gtm.element.dataset.gtmDatasourceId': props?.fields?.productInformation?.id,
                'gtm.element.dataset.gtmDatasourceName': props?.fields?.productInformation?.name,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductSingleCard;
